/**
 * Cases Filter Component
 *
 * Filters portfolio cases by industry and product with View Transition API animations.
 * Falls back to instant filtering for browsers that don't support View Transitions.
 */
export const casesFilter = () => ({
  selectedIndustry: "",
  selectedProduct: "",
  debug: false, // Enable with ?debug=true in URL

  /**
   * Initialize component and watch for filter changes
   */
  init() {
    // Check for debug query parameter
    const urlParams = new URLSearchParams(window.location.search);
    this.debug = urlParams.get("debug") === "true";

    this.$watch("selectedIndustry", () => this.filterItems());
    this.$watch("selectedProduct", () => this.filterItems());
  },

  /**
   * Filter items with View Transition API if supported
   */
  filterItems() {
    const applyFilter = () => {
      const cards = this.$el.querySelectorAll(".item-case");
      const grid = this.$el.querySelector(".archive-portfolio__grid");
      let visibleCount = 0;
      const debugData = [];

      // Toggle filtered class on grid
      const isFiltered = this.selectedIndustry || this.selectedProduct;
      if (grid) {
        grid.classList.toggle("is-filtered", isFiltered);
      }

      cards.forEach((card) => {
        const products = JSON.parse(card.dataset.products || "[]").map((id) => parseInt(id));
        const industries = JSON.parse(card.dataset.industries || "[]").map((id) => parseInt(id));
        const willShow = this.shouldShowCard(card);

        // Collect debug data if filters are active and debug mode is on
        if (this.debug && (this.selectedIndustry || this.selectedProduct)) {
          debugData.push({
            postId: card.dataset.postId,
            industries: industries.join(", ") || "none",
            products: products.join(", ") || "none",
            industryMatch: !this.selectedIndustry || industries.includes(parseInt(this.selectedIndustry)) ? "✓" : "✗",
            productMatch: !this.selectedProduct || products.includes(parseInt(this.selectedProduct)) ? "✓" : "✗",
            visible: willShow ? "✓" : "✗",
          });
        }

        if (willShow) {
          card.style.display = "";
          visibleCount++;
        } else {
          card.style.display = "none";
        }
      });

      // Display debug table if debug mode is enabled
      if (this.debug && debugData.length > 0) {
        console.table(debugData);
        console.log(
          `Filter: Industry=${this.selectedIndustry || "All"}, Product=${this.selectedProduct || "All"} → ${visibleCount} visible`,
        );
      }

      this.handleEmptyState(visibleCount);
    };

    // Use View Transition API if supported (Chrome/Edge)
    if (document.startViewTransition) {
      document.startViewTransition(applyFilter);
    } else {
      // Fallback for Firefox/Safari
      applyFilter();
    }
  },

  /**
   * Check if card should be visible based on current filters
   * Uses AND logic: must match both filters when both are selected
   *
   * @param {HTMLElement} card
   * @returns {boolean}
   */
  shouldShowCard(card) {
    const products = JSON.parse(card.dataset.products || "[]").map((id) => parseInt(id));
    const industries = JSON.parse(card.dataset.industries || "[]").map((id) => parseInt(id));

    // AND logic: match both filters if both are selected
    const industryMatch = !this.selectedIndustry || industries.includes(parseInt(this.selectedIndustry));

    const productMatch = !this.selectedProduct || products.includes(parseInt(this.selectedProduct));

    return industryMatch && productMatch;
  },

  /**
   * Show or hide empty state based on visible count
   *
   * @param {number} visibleCount
   */
  handleEmptyState(visibleCount) {
    const emptyState = this.$refs.emptyState;
    if (emptyState) {
      emptyState.style.display = visibleCount === 0 ? "block" : "none";
    }
  },

  /**
   * Reset all filters to default state
   */
  resetFilters() {
    this.selectedIndustry = "";
    this.selectedProduct = "";
  },
});
